/**
 * At-Risk Students API Service
 *
 * Aggregates risk signals across cohorts/labs so instructors don't have to
 * trawl every student detail page. The heuristic surfaces 6 signal types:
 *
 *   1. lab_time           — stuck on the same lab >2h with no submission
 *   2. low_pass_rate      — <50% pass across last 5 submissions
 *   3. missed_deadline    — cohort lab past due, no submission
 *   4. hand_raised        — explicit "I need help" flag (future signal —
 *                            requires platform support, see note below)
 *   5. inactive           — 3+ days idle while in an active cohort
 *   6. repeated_failures  — same lab failed 3+ times
 *
 * Multiple reasons aggregate into a single severity for sorting/display:
 *   - aggregateSeverity = max(reason.severity) with high > medium > low
 *   - ties broken by reason count (more reasons = pushed up one notch,
 *     capped at 'high'). This matches instructor intuition that 2 medium
 *     signals on the same student warrant the same attention as 1 high.
 *
 * Hand-raised note: the platform doesn't track explicit help requests
 * today. The type is included so the widget renders correctly once the
 * backend ships the signal, and a couple of mock entries demonstrate it.
 */

import { sessionManagerApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';

export type RiskReasonType =
  | 'lab_time'
  | 'low_pass_rate'
  | 'missed_deadline'
  | 'hand_raised'
  | 'inactive'
  | 'repeated_failures';

export type Severity = 'low' | 'medium' | 'high';

export interface RiskReason {
  type: RiskReasonType;
  severity: Severity;
  detail: string;
  firstDetectedAt: string;
}

export interface AtRiskStudent {
  studentId: string;
  studentName: string;
  studentEmail: string;
  avatarUrl?: string;
  cohortId?: string;
  cohortName?: string;
  currentLabId?: string;
  currentLabTitle?: string;
  lastActive: string;
  reasons: RiskReason[];
  aggregateSeverity: Severity;
}

export interface AtRiskFilters {
  cohortId?: string;
  severity?: Severity;
  reasonType?: RiskReasonType;
}

// ---------- helpers ----------

const SEVERITY_WEIGHT: Record<Severity, number> = { low: 1, medium: 2, high: 3 };

/**
 * Combine multiple reason severities into one. Max severity wins, with a
 * bump-up if there are 3+ reasons (signals piling up = more urgent).
 */
export function computeAggregateSeverity(reasons: RiskReason[]): Severity {
  if (reasons.length === 0) return 'low';
  const max = reasons.reduce(
    (acc, r) => (SEVERITY_WEIGHT[r.severity] > SEVERITY_WEIGHT[acc] ? r.severity : acc),
    'low' as Severity,
  );
  if (reasons.length >= 3 && max !== 'high') {
    return max === 'medium' ? 'high' : 'medium';
  }
  return max;
}

// Deterministic mock data — fixed timestamps relative to a stable anchor so
// the list ordering and "time ago" labels are predictable across renders.
// We use offsets from "now" but keep the offsets static, which is the
// closest deterministic approximation for relative-time UI.
const MS = 1000;
const MIN = 60 * MS;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const now = () => Date.now();

function mockStudents(): AtRiskStudent[] {
  const ts = now();
  const raw: Array<Omit<AtRiskStudent, 'aggregateSeverity'>> = [
    // ---- 3 high-severity ----
    {
      studentId: 's-201',
      studentName: 'Ayesha Khan',
      studentEmail: 'ayesha.khan@student.example.pk',
      cohortId: 'cohort-1',
      cohortName: 'Spring 2026 - LLM Fundamentals',
      currentLabId: 'lab-2-3-rag-pipeline',
      currentLabTitle: 'Building a RAG Pipeline',
      lastActive: new Date(ts - 4 * DAY).toISOString(),
      reasons: [
        {
          type: 'inactive',
          severity: 'high',
          detail: 'No activity for 4 days',
          firstDetectedAt: new Date(ts - 1 * DAY).toISOString(),
        },
        {
          type: 'missed_deadline',
          severity: 'high',
          detail: 'RAG Pipeline lab was due 2 days ago',
          firstDetectedAt: new Date(ts - 2 * DAY).toISOString(),
        },
        {
          type: 'low_pass_rate',
          severity: 'medium',
          detail: '2 of last 5 submissions passed (40%)',
          firstDetectedAt: new Date(ts - 3 * DAY).toISOString(),
        },
      ],
    },
    {
      studentId: 's-202',
      studentName: 'Bilal Ahmed',
      studentEmail: 'bilal.ahmed@student.example.pk',
      cohortId: 'cohort-2',
      cohortName: 'Spring 2026 - Advanced AI Engineering',
      currentLabId: 'lab-9-4-lora-basics',
      currentLabTitle: 'LoRA Fine-Tuning Basics',
      lastActive: new Date(ts - 15 * MIN).toISOString(),
      reasons: [
        {
          type: 'repeated_failures',
          severity: 'high',
          detail: 'Failed "LoRA Fine-Tuning Basics" 4 times',
          firstDetectedAt: new Date(ts - 8 * HOUR).toISOString(),
        },
        {
          type: 'hand_raised',
          severity: 'high',
          detail: 'Marked "I need help" 22 minutes ago',
          firstDetectedAt: new Date(ts - 22 * MIN).toISOString(),
        },
      ],
    },
    {
      studentId: 's-203',
      studentName: 'Fatima Sheikh',
      studentEmail: 'fatima.sheikh@student.example.pk',
      cohortId: 'cohort-1',
      cohortName: 'Spring 2026 - LLM Fundamentals',
      currentLabId: 'lab-1-9-testing-ai',
      currentLabTitle: 'Testing AI Code',
      lastActive: new Date(ts - 2 * HOUR).toISOString(),
      reasons: [
        {
          type: 'lab_time',
          severity: 'high',
          detail: 'On this lab for 5.5 hours without submitting',
          firstDetectedAt: new Date(ts - 5 * HOUR - 30 * MIN).toISOString(),
        },
        {
          type: 'hand_raised',
          severity: 'medium',
          detail: 'Marked "I need help" 1 hour ago',
          firstDetectedAt: new Date(ts - 1 * HOUR).toISOString(),
        },
      ],
    },

    // ---- 4 medium-severity ----
    {
      studentId: 's-204',
      studentName: 'Hassan Raza',
      studentEmail: 'hassan.raza@student.example.pk',
      cohortId: 'cohort-2',
      cohortName: 'Spring 2026 - Advanced AI Engineering',
      currentLabId: 'lab-9-5-qlora',
      currentLabTitle: 'QLoRA Quantization',
      lastActive: new Date(ts - 30 * MIN).toISOString(),
      reasons: [
        {
          type: 'low_pass_rate',
          severity: 'medium',
          detail: '1 of last 5 submissions passed (20%)',
          firstDetectedAt: new Date(ts - 6 * HOUR).toISOString(),
        },
      ],
    },
    {
      studentId: 's-205',
      studentName: 'Maryam Tariq',
      studentEmail: 'maryam.tariq@student.example.pk',
      cohortId: 'cohort-3',
      cohortName: 'Spring 2026 - Fullstack AI',
      currentLabId: 'lab-6-2-streaming-ui',
      currentLabTitle: 'Streaming AI Responses in the UI',
      lastActive: new Date(ts - 3 * HOUR).toISOString(),
      reasons: [
        {
          type: 'lab_time',
          severity: 'medium',
          detail: 'On this lab for 3.2 hours without submitting',
          firstDetectedAt: new Date(ts - 1 * HOUR - 12 * MIN).toISOString(),
        },
      ],
    },
    {
      studentId: 's-206',
      studentName: 'Omar Farooq',
      studentEmail: 'omar.farooq@student.example.pk',
      cohortId: 'cohort-1',
      cohortName: 'Spring 2026 - LLM Fundamentals',
      currentLabId: 'lab-2-1-prompt-eng',
      currentLabTitle: 'Prompt Engineering Patterns',
      lastActive: new Date(ts - 3 * DAY - 2 * HOUR).toISOString(),
      reasons: [
        {
          type: 'inactive',
          severity: 'medium',
          detail: 'No activity for 3 days',
          firstDetectedAt: new Date(ts - 2 * HOUR).toISOString(),
        },
      ],
    },
    {
      studentId: 's-207',
      studentName: 'Zainab Iqbal',
      studentEmail: 'zainab.iqbal@student.example.pk',
      cohortId: 'cohort-3',
      cohortName: 'Spring 2026 - Fullstack AI',
      currentLabId: 'lab-6-5-auth-ai',
      currentLabTitle: 'Authenticated AI Endpoints',
      lastActive: new Date(ts - 45 * MIN).toISOString(),
      reasons: [
        {
          type: 'repeated_failures',
          severity: 'medium',
          detail: 'Failed "Authenticated AI Endpoints" 3 times',
          firstDetectedAt: new Date(ts - 4 * HOUR).toISOString(),
        },
      ],
    },

    // ---- 3 low-severity ----
    {
      studentId: 's-208',
      studentName: 'Imran Malik',
      studentEmail: 'imran.malik@student.example.pk',
      cohortId: 'cohort-2',
      cohortName: 'Spring 2026 - Advanced AI Engineering',
      currentLabId: 'lab-9-9-evaluation-benchmarks',
      currentLabTitle: 'Evaluation Benchmarks',
      lastActive: new Date(ts - 6 * HOUR).toISOString(),
      reasons: [
        {
          type: 'lab_time',
          severity: 'low',
          detail: 'On this lab for 2.3 hours without submitting',
          firstDetectedAt: new Date(ts - 18 * MIN).toISOString(),
        },
      ],
    },
    {
      studentId: 's-209',
      studentName: 'Sana Yusuf',
      studentEmail: 'sana.yusuf@student.example.pk',
      cohortId: 'cohort-1',
      cohortName: 'Spring 2026 - LLM Fundamentals',
      currentLabId: 'lab-1-10-project-milestone',
      currentLabTitle: 'Project Milestone 1',
      lastActive: new Date(ts - 1 * HOUR).toISOString(),
      reasons: [
        {
          type: 'low_pass_rate',
          severity: 'low',
          detail: '2 of last 5 submissions passed (40%)',
          firstDetectedAt: new Date(ts - 50 * MIN).toISOString(),
        },
      ],
    },
    {
      studentId: 's-210',
      studentName: 'Usman Riaz',
      studentEmail: 'usman.riaz@student.example.pk',
      cohortId: 'cohort-3',
      cohortName: 'Spring 2026 - Fullstack AI',
      currentLabId: 'lab-6-8-deployment',
      currentLabTitle: 'Deploying AI Apps',
      lastActive: new Date(ts - 2 * DAY - 6 * HOUR).toISOString(),
      reasons: [
        {
          type: 'hand_raised',
          severity: 'low',
          detail: 'Marked "I need help" yesterday',
          firstDetectedAt: new Date(ts - 1 * DAY).toISOString(),
        },
      ],
    },
  ];

  return raw.map((s) => ({
    ...s,
    aggregateSeverity: computeAggregateSeverity(s.reasons),
  }));
}

// =====================================================
// BACKEND CONTRACT (session-manager /instructor/at-risk)
// =====================================================
//
// The signal endpoint returns a flat, string-reason shape:
//   { students: [{ student_id, name, cohort, risk_score,
//                  reasons: string[], last_active }], count }
// We map that into the richer AtRiskStudent the widget renders: reason
// strings are classified into RiskReasonType, and aggregateSeverity is
// derived from risk_score (per-reason severity mirrors it since the backend
// only ships one score per student).

interface BackendAtRiskStudent {
  student_id: string;
  name: string;
  cohort?: string | null;
  /** Cohort UUID — required by the dismiss endpoint (at_risk_dismissals.cohort_id). */
  cohort_id?: string | null;
  risk_score?: number;
  reasons?: string[];
  last_active?: string;
}

/** Normalise an unknown-scale risk score (0–1 or 0–100) to a severity band. */
function severityFromScore(score: number | undefined): Severity {
  if (typeof score !== 'number' || Number.isNaN(score)) return 'medium';
  const norm = score > 1 ? score / 100 : score;
  if (norm >= 0.66) return 'high';
  if (norm >= 0.33) return 'medium';
  return 'low';
}

/** Best-effort classification of a free-text reason into a RiskReasonType. */
function classifyReason(text: string): RiskReasonType {
  const t = text.toLowerCase();
  if (/deadline|overdue|past due|due /.test(t)) return 'missed_deadline';
  if (/inactive|no activity|idle|day/.test(t)) return 'inactive';
  if (/hand.?rais|need help|help/.test(t)) return 'hand_raised';
  if (/fail|repeated/.test(t)) return 'repeated_failures';
  if (/stuck|hour|without submit/.test(t)) return 'lab_time';
  if (/pass rate|mastery|score|%/.test(t)) return 'low_pass_rate';
  return 'lab_time';
}

function mapAtRiskStudent(raw: BackendAtRiskStudent): AtRiskStudent {
  const severity = severityFromScore(raw.risk_score);
  const lastActive = raw.last_active ?? new Date().toISOString();
  const reasons: RiskReason[] = (raw.reasons ?? []).map((detail) => ({
    type: classifyReason(detail),
    severity,
    detail,
    firstDetectedAt: lastActive,
  }));
  return {
    studentId: raw.student_id,
    studentName: raw.name,
    studentEmail: '',
    cohortId: raw.cohort_id ?? undefined,
    cohortName: raw.cohort ?? undefined,
    lastActive,
    reasons,
    // Trust the backend score for ordering rather than re-deriving from the
    // (uniform) per-reason severities.
    aggregateSeverity: severity,
  };
}

/**
 * Fetch the at-risk student list. Filters are applied server-side in real
 * mode; for the mock fallback we filter client-side to keep the behaviour
 * identical from the caller's perspective.
 */
export async function getAtRiskStudents(filters: AtRiskFilters = {}): Promise<AtRiskStudent[]> {
  try {
    const response = await sessionManagerApi.get('/instructor/at-risk', { params: filters });
    const students = (response.data.students ?? []) as BackendAtRiskStudent[];
    return students.map(mapAtRiskStudent);
  } catch (error) {
    console.error('Failed to fetch at-risk students:', error);
    let list = mockStudents();
    if (filters.cohortId) {
      list = list.filter((s) => s.cohortId === filters.cohortId);
    }
    if (filters.severity) {
      list = list.filter((s) => s.aggregateSeverity === filters.severity);
    }
    if (filters.reasonType) {
      list = list.filter((s) => s.reasons.some((r) => r.type === filters.reasonType));
    }
    return mockFallback(error, list);
  }
}

/**
 * Instructor dismisses a student from the at-risk list. The backend
 * (instructor-at-risk.ts:297) does a per-STUDENT 24h dismissal keyed by
 * (student_id, cohort_id) and expects `{ cohort_id, note }` — NOT a per-reason
 * body. Sending the wrong shape previously silently no-op'd the suppression.
 */
export async function dismissStudent(
  studentId: string,
  cohortId?: string,
  note?: string,
): Promise<void> {
  try {
    await sessionManagerApi.post(`/instructor/at-risk/${studentId}/dismiss`, {
      cohort_id: cohortId,
      note: note ?? 'Dismissed for 24h from instructor dashboard',
    });
  } catch (error) {
    // In mock mode (no backend), pretend it succeeded so the UI flow can
    // still be exercised end-to-end.
    if (process.env.REACT_APP_USE_MOCK_DATA === 'true') {
      return;
    }
    throw error;
  }
}
